import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL, useAuth } from "../../App";
import { toast } from "sonner";
import { QrCode, Maximize, Download, Printer, Copy, ExternalLink } from "lucide-react";

export default function CoupleTreeQR() {
  const { wedding } = useOutletContext();
  const [qrInfo, setQrInfo] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const containerRef = useRef(null);

  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const weddingId = wedding?.wedding_id;

  useEffect(() => {
    if (!weddingId) return;
    const fetchInfo = async () => {
      try {
        const r = await axios.get(`${API}/qr/${weddingId}/tree/info`, { headers });
        setQrInfo(r.data);
      } catch (e) {
        console.error("Failed to fetch QR info:", e);
      }
    };
    fetchInfo();
  }, [weddingId]);

  const qrImageUrl = weddingId ? `${API}/qr/${weddingId}/tree` : "";

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await containerRef.current?.requestFullscreen?.();
      setFullscreen(true);
    } else {
      await document.exitFullscreen?.();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const downloadQR = async () => {
    try {
      const response = await fetch(qrImageUrl, { headers });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wedding_tree_qr_${wedding?.slug || "code"}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("QR code downloaded");
    } catch {
      toast.error("Failed to download QR");
    }
  };

  const printQR = () => {
    const printWin = window.open("", "_blank", "width=600,height=800");
    if (!printWin) return;
    const coupleName = wedding ? `${wedding.couple_name_1} & ${wedding.couple_name_2}` : "";
    printWin.document.write(`<!DOCTYPE html><html><head><title>Wedding Tree QR - ${coupleName}</title>
      <style>
        body { margin: 0; padding: 40px; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: Georgia, serif; background: #fff; color: #111; }
        img { width: 400px; height: 400px; }
        h1 { margin-top: 30px; font-size: 28px; }
        p { font-size: 16px; color: #555; margin-top: 8px; }
        .url { font-size: 12px; color: #888; margin-top: 16px; word-break: break-all; max-width: 400px; text-align: center; }
        @media print { body { padding: 20px; } }
      </style></head><body>
      <img src="${qrImageUrl}" alt="QR Code" />
      <h1>${coupleName}</h1>
      <p>Scan to leave your video greeting</p>
      <p class="url">${qrInfo?.tree_url || ""}</p>
      <script>setTimeout(() => { window.print(); window.close(); }, 1000);</script>
      </body></html>`);
  };

  const copyUrl = () => {
    if (qrInfo?.tree_url) {
      navigator.clipboard.writeText(qrInfo.tree_url);
      toast.success("URL copied!");
    }
  };

  if (!wedding) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const coupleName = `${wedding.couple_name_1} & ${wedding.couple_name_2}`;

  return (
    <div ref={containerRef} className={`${fullscreen ? "fixed inset-0 z-50 bg-wedding-black flex items-center justify-center" : "p-6 lg:p-8 max-w-4xl"}`} data-testid="tree-qr-page">
      {!fullscreen && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-serif text-3xl text-wedding-ivory" data-testid="qr-title">Wedding Tree QR</h1>
              <p className="text-wedding-ivory/50 text-sm mt-1">Share this QR code so guests can submit their video greetings</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* QR Code Display */}
            <div className="flex flex-col items-center">
              <div className="p-8 bg-wedding-charcoal border border-white/5 flex flex-col items-center">
                {qrImageUrl && (
                  <img
                    src={qrImageUrl}
                    alt="Wedding Tree QR Code"
                    className="w-64 h-64 sm:w-80 sm:h-80"
                    data-testid="qr-image"
                  />
                )}
                <h2 className="font-serif text-xl text-wedding-ivory mt-6">{coupleName}</h2>
                <p className="text-wedding-gold text-sm mt-1">Scan to leave your greeting</p>
                {qrInfo?.tree_url && (
                  <p className="text-wedding-ivory/30 text-xs mt-3 text-center break-all max-w-[300px]">{qrInfo.tree_url}</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <button onClick={toggleFullscreen} className="w-full flex items-center gap-3 px-5 py-4 bg-wedding-charcoal border border-white/5 hover:border-wedding-gold/30 transition-colors text-left" data-testid="fullscreen-btn">
                <Maximize className="w-5 h-5 text-wedding-gold" />
                <div>
                  <p className="text-wedding-ivory font-semibold text-sm">Fullscreen Display</p>
                  <p className="text-wedding-ivory/40 text-xs">Show on TV or projector</p>
                </div>
              </button>

              <button onClick={downloadQR} className="w-full flex items-center gap-3 px-5 py-4 bg-wedding-charcoal border border-white/5 hover:border-wedding-gold/30 transition-colors text-left" data-testid="download-btn">
                <Download className="w-5 h-5 text-wedding-gold" />
                <div>
                  <p className="text-wedding-ivory font-semibold text-sm">Download PNG</p>
                  <p className="text-wedding-ivory/40 text-xs">High-quality QR image</p>
                </div>
              </button>

              <button onClick={printQR} className="w-full flex items-center gap-3 px-5 py-4 bg-wedding-charcoal border border-white/5 hover:border-wedding-gold/30 transition-colors text-left" data-testid="print-btn">
                <Printer className="w-5 h-5 text-wedding-gold" />
                <div>
                  <p className="text-wedding-ivory font-semibold text-sm">Print</p>
                  <p className="text-wedding-ivory/40 text-xs">Print-friendly QR with couple name</p>
                </div>
              </button>

              <button onClick={copyUrl} className="w-full flex items-center gap-3 px-5 py-4 bg-wedding-charcoal border border-white/5 hover:border-wedding-gold/30 transition-colors text-left" data-testid="copy-url-btn">
                <Copy className="w-5 h-5 text-wedding-gold" />
                <div>
                  <p className="text-wedding-ivory font-semibold text-sm">Copy URL</p>
                  <p className="text-wedding-ivory/40 text-xs">{qrInfo?.tree_url ? "Share the direct link" : "Loading..."}</p>
                </div>
              </button>

              {wedding.slug && (
                <a
                  href={`/${wedding.slug}/tree`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center gap-3 px-5 py-4 bg-wedding-charcoal border border-white/5 hover:border-wedding-gold/30 transition-colors text-left block"
                  data-testid="view-tree-btn"
                >
                  <ExternalLink className="w-5 h-5 text-wedding-gold" />
                  <div>
                    <p className="text-wedding-ivory font-semibold text-sm">View Live Tree</p>
                    <p className="text-wedding-ivory/40 text-xs">See the wedding tree display</p>
                  </div>
                </a>
              )}
            </div>
          </div>
        </>
      )}

      {/* Fullscreen QR View */}
      {fullscreen && (
        <div className="text-center cursor-pointer" onClick={toggleFullscreen}>
          <img src={qrImageUrl} alt="QR Code" className="w-[400px] h-[400px] mx-auto" />
          <h1 className="font-serif text-4xl text-wedding-ivory mt-8">{coupleName}</h1>
          <p className="text-wedding-gold text-xl mt-3">Scan to leave your video greeting</p>
          {qrInfo?.tree_url && (
            <p className="text-wedding-ivory/30 text-sm mt-6">{qrInfo.tree_url}</p>
          )}
          <p className="text-wedding-ivory/20 text-xs mt-12">Tap anywhere to exit fullscreen</p>
        </div>
      )}
    </div>
  );
}
